/* =============================================================================
   Eterno Grafiche — Configuratore Stampa su tela v2.0
   Preview live generata via CSS (nessun mockup fotografato) — la tela nuda
   usa un box con ombra/rilievo, la Cassetta (cornice a battente alto) usa
   bordo + intercapedine colorati in base ai dati reali delle aste.
   ============================================================================= */

(function () {
  'use strict';

  const LS_KEY = 'eterno_tela_v2';
  const STEPS  = ['formato-cornice', 'foto-checkout'];
  const CORNICE_NONE = 'Nessuna';

  // Limite Shopify per upload immagine come line item property (via /cart/add
  // multipart) — https://help.shopify.com/en/manual/shopify-admin/productivity-tools/file-uploads
  const MAX_FOTO_BYTES = 10 * 1024 * 1024;

  // Formato -> rapporto base/altezza, per l'aspect-ratio della preview.
  // Fonte: eternografiche_databases/db_stampe_tela.csv
  const FORMAT_RATIO = {
    '30×40 cm': 30 / 40,
    '30×45 cm': 30 / 45,
    '40×50 cm': 40 / 50,
    '40×60 cm': 40 / 60,
  };

  // Colore/larghezza della Cornice letti live dai data-attribute dello swatch
  // cliccato (a loro volta popolati da Liquid dal metaobject asta_configuratore)
  // — nessuna mappa JS da tenere allineata al catalogo.
  const GAP_PX_DEFAULT = 8; // gap "a giorno" della Cassetta (ombra), px fissi

  // ==========================================================================

  class EternoTela {

    constructor() {
      this.data = {
        formato:      null,
        orientamento: 'Verticale',
        cornice:      CORNICE_NONE,
        corniceStart: null,
        corniceEnd:   null,
        corniceMm:    25,
        corniceThumb: null,
        variantId:    null,
        prezzo:       0,
        hasFoto:     false,
        fotoDataUrl: null,
        quantita:    1,
        cNote:       '',
        privacy:     false,
      };
      this.stepIndex   = 0;
      this._toastTimer = null;
      this.init();
    }

    get currentStep() { return STEPS[this.stepIndex]; }

    // ---- Init ------------------------------------------------------------

    init() {
      const form = document.getElementById('ConfTelaForm');
      if (!form) return;

      this.bindFormatoPills();
      this.bindOrientPills();
      this.bindCorniceSwatches();
      this.bindFotoUpload();
      this.bindQty();
      this.bindCheckoutInputs();
      this.bindNav();

      if (this.loadFromStorage()) this.restoreFormFields();

      this.updateUI();
      this.updateBreadcrumb();
      this.updatePreview();
      this.updatePriceHint();
    }

    // ---- Formato ------------------------------------------------------------

    bindFormatoPills() {
      document.querySelectorAll('#ctela-formato-pills .ctela-pill').forEach(btn => {
        btn.addEventListener('click', () => this.onFormatoSelect(btn));
      });
    }

    // ---- Orientamento (proprietà, stesso prezzo) -----------------------------

    bindOrientPills() {
      document.querySelectorAll('#ctela-orient-pills .ctela-pill').forEach(btn => {
        btn.addEventListener('click', () => this._setOrientamento(btn.dataset.orient, true));
      });
    }

    _setOrientamento(orient, persist) {
      if (orient !== 'Verticale' && orient !== 'Orizzontale') return;
      this.data.orientamento = orient;
      document.querySelectorAll('#ctela-orient-pills .ctela-pill').forEach(b =>
        b.classList.toggle('is-selected', b.dataset.orient === orient));
      this.updatePreview();
      this.updateBreadcrumb();
      if (persist) this.saveToStorage();
    }

    // "30×40" con orientamento applicato -> [base, altezza] (swap se orizzontale)
    _orientedCm(formato) {
      let [b, a] = this._formatCm(formato);
      if (this.data.orientamento === 'Orizzontale') { const t = b; b = a; a = t; }
      return [b, a];
    }

    onFormatoSelect(btn) {
      document.querySelectorAll('#ctela-formato-pills .ctela-pill').forEach(b => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');
      this.data.formato = btn.dataset.formato || null;
      this.recomputeVariant();
      this.updatePreview();
      this.updateBreadcrumb();
      this.updatePriceHint();
      this.saveToStorage();
    }

    // ---- Cornice ------------------------------------------------------------

    bindCorniceSwatches() {
      document.querySelectorAll('.ctela-swatch').forEach(btn => {
        btn.addEventListener('click', () => this.onCorniceSelect(btn));
      });
    }

    onCorniceSelect(btn) {
      document.querySelectorAll('.ctela-swatch').forEach(b => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');
      this.data.cornice      = btn.dataset.cornice || CORNICE_NONE;
      this.data.corniceStart = btn.dataset.coloreStart || null;
      this.data.corniceEnd   = btn.dataset.coloreEnd || null;
      this.data.corniceMm    = parseFloat(btn.dataset.larghezzaMm) || 25;
      this.data.corniceThumb = btn.dataset.thumbUrl || null;
      this.recomputeVariant();
      this.updatePreview();
      this.updateBreadcrumb();
      this.updatePriceHint();
      this.saveToStorage();
    }

    // ---- Variante + prezzo dal formato+cornice selezionati -------------------

    recomputeVariant() {
      const d = this.data;
      if (!d.formato) { d.variantId = null; d.prezzo = 0; return; }
      const key   = d.formato + ' / ' + d.cornice;
      const entry = window.EternoTelaVariants && window.EternoTelaVariants[key];
      d.variantId = entry ? entry.id : null;
      d.prezzo    = entry && typeof entry.price === 'number' ? entry.price : 0;
    }

    // ---- Foto upload ---------------------------------------------------------

    bindFotoUpload() {
      // Due input file: quello del form (name="properties[Foto]") e uno comodo
      // nella colonna anteprima. Entrambi passano dallo stesso handler; il file
      // finisce SEMPRE anche nell'input del form (via DataTransfer) così il
      // carrello riceve la foto qualunque sia l'origine.
      const formInput = document.getElementById('ctela-foto-upload');
      ['ctela-foto-upload', 'ctela-foto-upload-preview'].forEach(id => {
        const input = document.getElementById(id);
        if (!input) return;
        input.addEventListener('change', () => {
          const file = input.files && input.files[0];
          if (!file || !file.type.startsWith('image/')) return;
          this._handleFotoFile(file, input === formInput);
        });
      });

      // Click sullo stage: se non c'è foto → apri il file picker; se c'è → zoom.
      const stage = document.getElementById('ctela-stage');
      if (stage) {
        const onStage = () => {
          if (this.data.hasFoto) this._openZoom();
          else document.getElementById('ctela-foto-upload-preview')?.click();
        };
        stage.addEventListener('click', onStage);
        stage.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onStage(); }
        });
      }
      document.getElementById('ctela-preview-upload-btn')?.addEventListener('click', () => {
        document.getElementById('ctela-foto-upload-preview')?.click();
      });

      // Lightbox: chiusura via backdrop / bottone / Esc
      document.getElementById('ctela-lightbox')?.addEventListener('click', e => {
        if (e.target.closest('[data-lightbox-close]')) this._closeZoom();
      });
      document.addEventListener('keydown', e => { if (e.key === 'Escape') this._closeZoom(); });
    }

    _handleFotoFile(file, fromFormInput) {
      const formInput = document.getElementById('ctela-foto-upload');
      if (formInput && !fromFormInput && typeof DataTransfer !== 'undefined') {
        try {
          const dt = new DataTransfer();
          dt.items.add(file);
          formInput.files = dt.files;
        } catch (_) { /* browser senza DataTransfer settabile */ }
      }
      const reader = new FileReader();
      reader.onload = e => {
        this.data.fotoDataUrl = e.target.result;
        this.data.hasFoto     = true;
        // Auto-orientamento: imposta V/H in base alle proporzioni della foto
        // (resta modificabile a mano dopo). Quadrata → resta com'è.
        const probe = new Image();
        probe.onload = () => {
          if (probe.naturalWidth && probe.naturalHeight && probe.naturalWidth !== probe.naturalHeight) {
            this._setOrientamento(probe.naturalWidth > probe.naturalHeight ? 'Orizzontale' : 'Verticale', true);
          }
        };
        probe.src = e.target.result;
        this._applyFotoToPreview(e.target.result);

        const label = document.getElementById('ctela-upload-label');
        if (label) {
          label.classList.add('has-foto');
          const txt = document.getElementById('ctela-upload-label-text');
          if (txt) txt.textContent = 'Foto caricata — clicchi per cambiarla';
        }
        this._syncPreviewUploadState();
        this.updateBreadcrumb();
        this.saveToStorage();
      };
      reader.readAsDataURL(file);
    }

    _applyFotoToPreview(dataUrl) {
      const layer = document.getElementById('ctela-foto-layer');
      if (!layer) return;

      let img = document.getElementById('ctela-user-photo');
      if (!img) {
        img = document.createElement('img');
        img.id  = 'ctela-user-photo';
        img.alt = 'La foto caricata';
        layer.appendChild(img);
      }
      img.src = dataUrl;

      this._syncPreviewUploadState();
      this.updatePreview();
    }

    // Sincronizza gli inviti al caricamento in base allo stato foto e allo step.
    // Regola anti-ridondanza: mai più di UN invito "carica" visibile alla volta —
    // step 1 senza foto → pill sullo stage; step 2 senza foto → solo l'area
    // tratteggiata del form (pill nascosta); con foto → thumbnail nell'area
    // upload + "Cambia foto" sotto l'anteprima + zoom sullo stage.
    _syncPreviewUploadState() {
      const has     = this.data.hasFoto;
      const inStep2 = this.currentStep === 'foto-checkout';
      const stage   = document.getElementById('ctela-stage');
      const cta     = document.getElementById('ctela-stage-cta');
      const zoom    = document.getElementById('ctela-stage-zoom');
      const actions = document.getElementById('ctela-preview-actions');
      const btn     = document.getElementById('ctela-preview-upload-btn');
      const thumb   = document.getElementById('ctela-upload-thumb');
      if (stage) stage.classList.toggle('has-foto', has);
      if (cta)  cta.style.display  = (has || inStep2) ? 'none' : '';
      if (zoom) zoom.style.display = has ? '' : 'none';
      if (actions) actions.hidden = !has;
      if (btn) btn.textContent = has ? 'Cambi la foto' : 'Carichi la foto';
      if (thumb) {
        if (has && this.data.fotoDataUrl) { thumb.src = this.data.fotoDataUrl; thumb.hidden = false; }
        else { thumb.hidden = true; thumb.removeAttribute('src'); }
      }
    }

    _openZoom() {
      if (!this.data.hasFoto) return;
      const lb    = document.getElementById('ctela-lightbox');
      const stage = document.getElementById('ctela-lightbox-stage');
      const src   = document.getElementById('ctela-frame-box');
      if (!lb || !stage || !src) return;
      stage.innerHTML = '';
      const clone = src.cloneNode(true);
      clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
      clone.removeAttribute('id');
      clone.querySelectorAll('.tela-stage__cta, .tela-stage__zoom').forEach(el => el.remove());
      // Larghezza esplicita dello stage in base al rapporto del box, così il
      // riquadro incorniciato sta nel viewport per qualunque formato (i
      // portrait alti non escono più). height = width/ratio ≤ 86vh.
      const ratio = parseFloat(src.style.getPropertyValue('--ratio')) || 0.75;
      const w = Math.min(window.innerWidth * 0.88, window.innerHeight * 0.86 * ratio);
      stage.style.width = Math.round(w) + 'px';
      stage.appendChild(clone);
      lb.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    _closeZoom() {
      const lb = document.getElementById('ctela-lightbox');
      if (lb && !lb.hidden) { lb.hidden = true; document.body.style.overflow = ''; }
    }

    // ---- Quantità ------------------------------------------------------------

    bindQty() {
      const input = document.getElementById('ct-qty');
      if (!input) return;

      const clamp = () => {
        const v = Math.max(1, parseInt(input.value) || 1);
        input.value        = v;
        this.data.quantita = v;
        this.updatePriceHint();
        this.updateBreadcrumb();
        this.saveToStorage();
      };

      document.getElementById('ctela-qty-minus')?.addEventListener('click', () => {
        input.value = Math.max(1, parseInt(input.value || 1) - 1);
        clamp();
      });
      document.getElementById('ctela-qty-plus')?.addEventListener('click', () => {
        input.value = parseInt(input.value || 1) + 1;
        clamp();
      });
      input.addEventListener('change', clamp);
    }

    // ---- Checkout inputs -----------------------------------------------------

    bindCheckoutInputs() {
      const noteEl = document.getElementById('cc-note');
      if (noteEl) noteEl.addEventListener('input', () => {
        this.data.cNote = noteEl.value;
        this.updateLiveRecap();
        this.saveToStorage();
      });

      const privacyEl = document.getElementById('cc-privacy');
      if (privacyEl) privacyEl.addEventListener('change', () => {
        this.data.privacy = privacyEl.checked;
        this.saveToStorage();
      });
    }

    // ---- Navigation ----------------------------------------------------------

    bindNav() {
      document.getElementById('ctela-next')?.addEventListener('click', () => this.next());
      document.getElementById('ctela-prev')?.addEventListener('click', () => this.prev());
      document.getElementById('ctela-submit')?.addEventListener('click', () => this.addToCart());
    }

    next() {
      if (!this.validate()) return;
      if (this.stepIndex < STEPS.length - 1) {
        this.stepIndex++;
        this.updateUI();
        this.saveToStorage();
        this._scrollToConf();
      }
    }

    prev() {
      if (this.stepIndex > 0) {
        this.stepIndex--;
        this.clearError();
        this.updateUI();
        this.saveToStorage();
        this._scrollToConf();
      }
    }

    _scrollToConf() {
      const el = document.getElementById('EternoTelaConf');
      if (!el) return;
      const headerEl = document.querySelector('sticky-header') || document.querySelector('.shopify-section-group-header-group');
      const headerH  = headerEl ? headerEl.offsetHeight : 0;
      const top      = el.getBoundingClientRect().top + window.scrollY - headerH - 16;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    // ---- Validation ------------------------------------------------------

    validate() {
      const s   = this.currentStep;
      const err = msg => { this.showError(msg); return false; };

      if (s === 'formato-cornice') {
        if (!this.data.formato) return err('Selezioni un formato per proseguire.');
        if (!this.data.variantId) return err('Configurazione non disponibile — ricarichi la pagina e riprovi.');
        this.clearError(); return true;
      }

      if (s === 'foto-checkout') {
        if (!this.data.hasFoto) return err('Carichi la foto prima di proseguire.');

        const qty = parseInt(document.getElementById('ct-qty')?.value);
        if (!qty || qty < 1) return err('La quantità minima è 1.');
        this.data.quantita = qty;

        const privacy = document.getElementById('cc-privacy')?.checked;
        if (!privacy) return err('Per procedere è necessario accettare la Privacy Policy.');

        this.data.cNote     = document.getElementById('cc-note')?.value.trim() || '';
        this.data.privacy   = privacy;
        this.clearError(); return true;
      }

      this.clearError();
      return true;
    }

    // ---- Preview live --------------------------------------------------------

    // "30×40 cm" -> [30, 40] (accetta × o x). Fallback [30,40].
    _formatCm(formato) {
      const m = String(formato || '').match(/(\d+(?:[.,]\d+)?)\s*[×x]\s*(\d+(?:[.,]\d+)?)/i);
      if (!m) return [30, 40];
      return [parseFloat(m[1].replace(',', '.')), parseFloat(m[2].replace(',', '.'))];
    }

    updatePreview() {
      const { formato, cornice, corniceStart, corniceEnd, corniceMm, corniceThumb } = this.data;

      const ph    = document.getElementById('ctela-preview-ph');
      const stage = document.getElementById('ctela-stage');
      const box   = document.getElementById('ctela-frame-box');

      if (!formato) {
        ph?.classList.remove('conf-hidden');
        stage?.classList.add('conf-hidden');
        return;
      }

      ph?.classList.add('conf-hidden');
      stage?.classList.remove('conf-hidden');
      this._syncPreviewUploadState();

      if (box) {
        const isNessuna = !cornice || cornice === CORNICE_NONE;
        box.dataset.cornice = isNessuna ? 'nessuna' : 'cassetta';
        box.classList.toggle('has-cornice', !isNessuna);

        // Geometria proporzionale (come Fotorecord: misure dai mm reali con
        // un'unica scala). Larghezza cornice in cqw = % della larghezza del box.
        // _orientedCm applica l'orientamento (swap base↔altezza se orizzontale).
        const [baseCm, altCm] = this._orientedCm(formato);
        const sezMm  = (!isNessuna) ? (corniceMm || 0) : 0;
        const totalW = baseCm * 10 + 2 * sezMm;
        const totalH = altCm * 10 + 2 * sezMm;
        box.style.setProperty('--ratio', totalW && totalH ? (totalW / totalH) : (FORMAT_RATIO[formato] || 1));

        if (!isNessuna && corniceStart && corniceEnd) {
          box.style.setProperty('--moulding-start', corniceStart);
          box.style.setProperty('--moulding-end', corniceEnd);
          box.style.setProperty('--moulding-w', (totalW ? (sezMm / totalW * 100) : 4) + 'cqw');
          box.style.setProperty('--gap-w', GAP_PX_DEFAULT + 'px');
          box.classList.toggle('has-real-image', !!corniceThumb);
          if (corniceThumb) {
            box.style.setProperty('--moulding-image', 'url("' + corniceThumb + '")');
            // 9-slice come Fotorecord: bordo cornice = larghezza_mm/200 del
            // render → slice% = mm/2. Angoli mitrati reali + lati, centro
            // (watermark) scartato.
            box.style.setProperty('--moulding-slice', (corniceMm / 2) + '%');
          }
        } else {
          box.classList.remove('has-real-image');
        }
      }

      const hint = document.getElementById('ctela-preview-hint');
      if (hint) {
        hint.textContent = this.data.hasFoto
          ? ''
          : 'Carichi la foto per vedere l\'anteprima completa';
      }
    }

    // ---- Price hint ------------------------------------------------------

    updatePriceHint() {
      const el = document.getElementById('ctela-price-hint');
      if (!el) return;
      const { prezzo, quantita } = this.data;
      if (!prezzo) { el.textContent = ''; return; }
      const tot = (prezzo * quantita).toFixed(2).replace('.', ',');
      const ppu = prezzo.toFixed(2).replace('.', ',');
      el.textContent = `Totale: €${tot} (${quantita} pz × €${ppu}/pz)`;
    }

    // ---- Breadcrumb --------------------------------------------------------

    updateBreadcrumb() {
      const el = document.getElementById('ctela-breadcrumb');
      if (!el) return;
      const d = this.data;

      const items = [
        d.formato,
        d.formato && d.orientamento,
        d.cornice !== CORNICE_NONE && d.cornice,
        d.hasFoto && 'Foto caricata',
        d.quantita && d.quantita > 1 && (d.quantita + ' pz'),
      ].filter(Boolean);

      if (!items.length) { el.innerHTML = ''; return; }

      el.innerHTML = items.map((item, i) =>
        '<span class="conf-breadcrumb__item">' + this.esc(item) + '</span>' +
        (i < items.length - 1 ? '<span class="conf-breadcrumb__sep" aria-hidden="true">›</span>' : '')
      ).join('');
    }

    // ---- Live recap (checkout step) -----------------------------------------

    updateLiveRecap() {
      if (this.currentStep !== 'foto-checkout') return;
      const inner = document.getElementById('ctela-recap-inner');
      if (!inner) return;
      inner.innerHTML = this.buildRecapHTML();
    }

    buildRecapHTML() {
      const d    = this.data;
      const tot  = d.prezzo ? '€' + (d.prezzo * d.quantita).toFixed(2).replace('.', ',') +
                   ' (' + d.quantita + ' pz × €' + d.prezzo.toFixed(2).replace('.', ',') + '/pz)' : '—';
      let html = '';

      html += this.recapSection('Tela', [
        ['Formato',      d.formato],
        ['Orientamento', d.orientamento],
        d.cornice !== CORNICE_NONE && ['Cornice', d.cornice],
        ['Quantità',     d.quantita + ' pz'],
        ['Totale',       tot],
      ].filter(Boolean));

      if (d.cNote) html += this.recapSection('Note', [['Note', d.cNote]]);

      return html;
    }

    recapSection(title, rows) {
      let dl = '<div class="conf-recap__section">' +
               '<h3 class="conf-recap__heading">' + this.esc(title) + '</h3>' +
               '<dl class="conf-recap__list">';
      rows.forEach(([dt, dd]) => {
        if (!dd) return;
        dl += '<dt>' + this.esc(dt) + '</dt><dd>' + this.esc(String(dd)) + '</dd>';
      });
      return dl + '</dl></div>';
    }

    // ---- UI update -----------------------------------------------------------

    updateUI() {
      const current = this.currentStep;
      const total   = STEPS.length;
      const index   = this.stepIndex;

      document.querySelectorAll('.conf-step').forEach(el => {
        const active = el.dataset.step === current;
        el.classList.toggle('conf-step--active', active);
        el.style.display = active ? '' : 'none';
      });

      const pct  = Math.round(((index + 1) / total) * 100);
      const fill = document.getElementById('ctela-bar');
      if (fill) fill.style.width = pct + '%';
      this._setText('ctela-bar-curr', index + 1);
      this._setText('ctela-bar-tot',  total);

      const prevBtn   = document.getElementById('ctela-prev');
      const nextBtn   = document.getElementById('ctela-next');
      const submitBtn = document.getElementById('ctela-submit');
      const isLast    = index === total - 1;

      if (prevBtn) prevBtn.disabled = index === 0;
      nextBtn?.classList.toggle('conf-hidden',   isLast);
      submitBtn?.classList.toggle('conf-hidden', !isLast);

      this.clearError();
      this.updateBreadcrumb();
      this.updatePreview();
      if (isLast) {
        this.updateLiveRecap();
        this.updatePriceHint();
      }
    }

    // ---- Aggiungi al carrello (upload nativo Shopify, form multipart reale) --

    addToCart() {
      if (!this.validate()) return;

      const d = this.data;
      if (!d.variantId) {
        this.showError('Configurazione non disponibile — ricarichi la pagina e riprovi.');
        return;
      }

      const fotoInput = document.getElementById('ctela-foto-upload');
      const fotoFile  = fotoInput && fotoInput.files && fotoInput.files[0];
      if (fotoFile && fotoFile.size > MAX_FOTO_BYTES) {
        this.showError('La foto supera i 10 MB consentiti: scelga un file più leggero oppure la invii a ' +
          document.getElementById('EternoTelaConf').dataset.email + ' dopo l\'ordine.');
        return;
      }

      const btn = document.getElementById('ctela-submit');
      if (btn) { btn.disabled = true; btn.textContent = 'Invio in corso…'; }
      this.clearError();

      const props = { 'Formato': d.formato, 'Orientamento': d.orientamento };
      if (d.cornice !== CORNICE_NONE) props['Cornice'] = d.cornice;
      if (d.cNote) props['Note'] = d.cNote;

      // Il file (se presente) è già collegato a #shopify-cart-form-tela via
      // l'attributo form="shopify-cart-form-tela" sull'input #ctela-foto-upload:
      // Shopify lo carica sul proprio CDN e lo lega alla riga d'ordine come
      // properties[Foto]. Richiede una submit di form reale — l'API AJAX
      // /cart/add.js non supporta upload di file.
      const form = document.getElementById('shopify-cart-form-tela');
      form.querySelector('#scf-tela-variant-id').value = d.variantId;
      form.querySelector('#scf-tela-quantity').value   = d.quantita;
      const propsContainer = form.querySelector('#scf-tela-properties');
      propsContainer.innerHTML = '';
      Object.entries(props).forEach(([k, v]) => {
        const input = document.createElement('input');
        input.type  = 'hidden';
        input.name  = 'properties[' + k + ']';
        input.value = v;
        propsContainer.appendChild(input);
      });

      try { sessionStorage.removeItem(LS_KEY); } catch (_) {}
      form.submit();
    }

    // ---- sessionStorage ------------------------------------------------------

    saveToStorage() {
      try {
        const payload = {
          data:      Object.assign({}, this.data, { fotoDataUrl: null }),
          stepIndex: this.stepIndex,
        };
        sessionStorage.setItem(LS_KEY, JSON.stringify(payload));
      } catch (_) {}
    }

    loadFromStorage() {
      try {
        const raw = sessionStorage.getItem(LS_KEY);
        if (!raw) return false;
        const saved = JSON.parse(raw);
        if (!saved || !saved.data) return false;
        Object.assign(this.data, saved.data);
        this.stepIndex = Math.min(saved.stepIndex || 0, STEPS.length - 1);
        return true;
      } catch (_) { return false; }
    }

    restoreFormFields() {
      const d = this.data;

      const set = (id, val) => {
        if (val === null || val === undefined || val === '') return;
        const el = document.getElementById(id);
        if (el) el.value = val;
      };

      set('ct-qty',   d.quantita);
      set('cc-note',  d.cNote);

      const cb = (id, val) => { const el = document.getElementById(id); if (el) el.checked = !!val; };
      cb('cc-privacy',   d.privacy);

      if (d.formato) {
        document.querySelectorAll('#ctela-formato-pills .ctela-pill').forEach(b => {
          b.classList.toggle('is-selected', b.dataset.formato === d.formato);
        });
      }
      document.querySelectorAll('#ctela-orient-pills .ctela-pill').forEach(b => {
        b.classList.toggle('is-selected', b.dataset.orient === (d.orientamento || 'Verticale'));
      });
      document.querySelectorAll('.ctela-swatch').forEach(b => {
        const match = (b.dataset.cornice || CORNICE_NONE) === (d.cornice || CORNICE_NONE);
        b.classList.toggle('is-selected', match);
        if (match && b.dataset.cornice && b.dataset.cornice !== CORNICE_NONE) {
          d.corniceStart = b.dataset.coloreStart || null;
          d.corniceEnd   = b.dataset.coloreEnd || null;
          d.corniceMm    = parseFloat(b.dataset.larghezzaMm) || 25;
          d.corniceThumb = b.dataset.thumbUrl || null;
        }
      });
      this.recomputeVariant();

      // La foto non sopravvive al reload (fotoDataUrl non persistito + input file
      // svuotato) → resetta lo stato per non mostrare "Foto caricata" a vuoto.
      if (!d.fotoDataUrl) d.hasFoto = false;
      if (d.hasFoto) {
        const label = document.getElementById('ctela-upload-label');
        if (label) {
          label.classList.add('has-foto');
          const txt = document.getElementById('ctela-upload-label-text');
          if (txt) txt.textContent = 'Foto caricata — clicchi per cambiarla';
        }
      }
      this._syncPreviewUploadState();
    }

    // ---- Error / Toast ---------------------------------------------------

    showError(msg) {
      const el = document.getElementById('ctela-error');
      if (el) { el.textContent = msg; el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }
    }

    clearError() {
      const el = document.getElementById('ctela-error');
      if (el) el.textContent = '';
    }

    showToast(msg, duration = 3500) {
      const el = document.getElementById('ctela-toast');
      if (!el) return;
      el.textContent = msg;
      el.classList.add('conf-toast--visible');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => el.classList.remove('conf-toast--visible'), duration);
    }

    // ---- Helpers -----------------------------------------------------------

    _setText(id, text) {
      const el = document.getElementById(id);
      if (el) el.textContent = text;
    }

    esc(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }
  }

  // ==========================================================================
  document.addEventListener('DOMContentLoaded', () => {
    if (document.getElementById('ConfTelaForm')) new EternoTela();
  });

})();
